import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { Amplify } from 'aws-amplify';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import { AMPLIFY_CONFIG } from '../config/settings';

export interface AuthUser {
  userId: string;
  username: string;
  signInDetails?: {
    loginId?: string;
    authFlowType?: string;
  };
  groups: string[];
}

interface AuthContextType {
  user: AuthUser | null;
  setUser: (user: AuthUser | null) => void;
  loading: boolean;
  groupsLoading: boolean;
  refreshAuth: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [groupsLoading] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      Amplify.configure(AMPLIFY_CONFIG);
      checkAuthState();
    } else {
      setLoading(false);
    }
  }, []);

  const checkAuthState = async () => {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();
      const tokens = session.tokens;

      if (tokens?.accessToken) {
        const groups =
          (tokens.accessToken.payload['cognito:groups'] as string[]) || [];

        const authUser: AuthUser = {
          userId: currentUser.userId,
          username: currentUser.username,
          signInDetails: currentUser.signInDetails as AuthUser['signInDetails'],
          groups,
        };

        setUser(authUser);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, setUser, loading, groupsLoading, refreshAuth: checkAuthState }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
